import { useState, useEffect, useRef, useMemo } from 'react'
import {
  useParams,
  useNavigate,
  useSearchParams,
  useBlocker,
} from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import { FileUpload } from '@/components/ui/file-upload'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbRouterLink,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { PATHROUTES } from '@/constants/path'
import { EyeOpenIcon } from '../Icons'
import { Info, Loader, Maximize2 } from 'lucide-react'
import { showErrorToast, showSuccessToast } from '@/components/ui/toast'
import AdFormatSpecificationsDialog from './AdFormatSpecificationsDialog'
import HomeHeroSectionBanner from './banners/HomeHeroSectionBanner'
import HomeInFeedNativeCardBanner from './banners/HomeInFeedNativeCardBanner'
import InlineBanner from './banners/InlineBanner'
import { useMutation, useQuery } from '@tanstack/react-query'
import useImageUpload from '@/hooks/useImageUpload'
import {
  createSponsorEventAdvertisement,
  getAdPlacementTypes,
  getAdvertisementDetail,
  getAvailableADDates,
  updateAdvertisement,
  type SponsorEventAdvertisementPayload,
} from '@/api/sponsorApi'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { getPlainTextLength, handleAxiosError } from '@/utils/helper'
import { createAdDateValidator } from '@/utils/dateValidation'
import {
  uploadAdSchema,
  type AdvertisementData,
  type AvailableADDatesType,
  type UploadAdFormData,
} from '@/schemas/sponsor.schems'
import {
  EVENT_MEDIA_OPTIONS,
  eventImageSize,
  eventImageVideoTypes,
  fileTypes,
  getMediaType,
  getNormalizedAdDates,
  isVideoFile,
  sponsorLogoSize,
  type EventMediaType,
} from '@/constants/sponsor'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import CustomLoader from '../CustomLoader'
import CustomRichTextEditor from '../CustomRichTextEditor'
import { Switch } from '../ui/switch'
import type { Position } from './DraggableText'
import { queryClient } from '@/main'

interface BannerData {
  adName?: string
  adDescription?: string
  linkText?: string
  redirectionLink?: string
  sponsorLogoUrl?: string | null
  eventImageUrl?: string | null
  eventMediaType?: EventMediaType
}

interface AdPlacementType {
  key: string
  placement: string
}

function UploadAdData() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [sponsorLogoUrl, setSponsorLogoUrl] = useState<string | null>(null)
  const [eventImageUrl, setEventImageUrl] = useState<string | null>(null)
  const [eventMediaType, setEventMediaType] = useState<EventMediaType>(
    EVENT_MEDIA_OPTIONS.IMAGE
  )
  const [isAdFormatDialogOpen, setIsAdFormatDialogOpen] = useState(false)
  const [showBlockDialog, setShowBlockDialog] = useState(false)
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [isHeroPreviewOpen, setIsHeroPreviewOpen] = useState(false)
  const navigationSuccessRef = useRef(false)
  const [searchParams] = useSearchParams()
  const adId = searchParams.get('adId')
  const sponsorShipId = searchParams.get('sponsorshipId')

  const [elementPositions, setElementPositions] = useState<{
    namePosition?: Position
    descriptionPosition?: Position
    sponsorLogoPosition?: Position
    linkPosition?: Position
  }>({})

  const [initialElementPositions, setInitialElementPositions] = useState<{
    namePosition?: Position
    descriptionPosition?: Position
    sponsorLogoPosition?: Position
    linkPosition?: Position
  }>({})

  const form = useForm<UploadAdFormData>({
    resolver: zodResolver(uploadAdSchema),
    defaultValues: {
      adName: '',
      adDescription: '',
      redirectionLink: '',
      linkText: '',
      adType: '',
      showTitle: true,
      showDescription: true,
    },
    mode: 'onChange',
  })

  const { data: advertisementData, isLoading: isLoadingAdData } = useQuery({
    queryKey: SPONSOR_QUERY_KEYS.advertisementById(adId as string),
    queryFn: () => getAdvertisementDetail({ id: adId as string }),
    enabled: !!adId,
  })

  const { data: adPlacementTypesData } = useQuery({
    queryKey: SPONSOR_QUERY_KEYS.adPlacementTypes(id as string),
    queryFn: () => getAdPlacementTypes({ sponsorshipId: id as string }),
    enabled: !!id,
  })

  const {
    uploadFiles: uploadSponsorLogo,
    isUploading: isUploadingSponsorLogo,
    getExistingImageFiles: getExistingSponsorLogoFile,
  } = useImageUpload({
    linkFor: 'advertisementSponsorLogo',
    maxFiles: 1,
  })

  const {
    uploadFiles: uploadAdvertisementEventImage,
    isUploading: isUploadingEventImage,
    getExistingImageFiles: getExistingEventImageFile,
  } = useImageUpload({
    linkFor: 'advertisementEventImage',
    maxFiles: 1,
    allowedTypes: eventImageVideoTypes,
  })

  const {
    mutate: createSponsorEventAdvertisementMutation,
    isPending: isCreatingAdvertisement,
  } = useMutation({
    mutationFn: createSponsorEventAdvertisement,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: SPONSOR_QUERY_KEYS.advertisementById(adId as string),
      })
      navigationSuccessRef.current = true
      showSuccessToast(
        'Ad Uploaded Successfully',
        "Your ad has been submitted and is now pending approval. You'll be notified once it's live."
      )
      const redirectionId = sponsorShipId || id
      navigate(
        PATHROUTES.DASHBOARD.SPONSORSHIP_DETAILS_WITH_ID(redirectionId || '')
      )
    },
    onError: err => {
      handleAxiosError('Failed to upload advertisement.', err)
    },
  })

  const {
    mutate: updateAdvertisementMutation,
    isPending: isUpdatingAdvertisement,
  } = useMutation({
    mutationFn: updateAdvertisement,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: SPONSOR_QUERY_KEYS.advertisementById(adId as string),
      })
      navigationSuccessRef.current = true
      showSuccessToast('Your ad has been updated successfully.')
      const redirectionId = sponsorShipId || id
      navigate(
        PATHROUTES.DASHBOARD.SPONSORSHIP_DETAILS_WITH_ID(redirectionId || '')
      )
    },
    onError: err => {
      handleAxiosError('Failed to update advertisement.', err)
    },
  })

  useEffect(() => {
    const loadData = async () => {
      if (advertisementData) {
        setIsDataLoading(true)
        try {
          const adData: AdvertisementData = advertisementData?.data
          let sponsorLogo = null
          if (adData?.sponsorLogo) {
            sponsorLogo = await getExistingSponsorLogoFile([adData.sponsorLogo])
          }
          const eventImage = await getExistingEventImageFile([
            adData.eventImage,
          ])

          const originalStartDate = new Date(adData.startDate)
          const originalEndDate = new Date(adData.endDate)

          form.reset({
            adName: adData.name,
            adDescription: adData.description,
            adStartDate: originalStartDate,
            adEndDate: originalEndDate,
            adType: adData.placement,
            redirectionLink: adData.link,
            linkText: adData.linkText,
            ...(sponsorLogo && { sponsorLogo: sponsorLogo[0] }),
            eventImage: eventImage[0],
            showTitle: adData.showTitle,
            showDescription: adData.showDescription,
          })

          if (adData?.sponsorLogo) {
            setSponsorLogoUrl(adData?.sponsorLogo?.url)
          }
          setEventImageUrl(adData.eventImage.url)
          const positions = {
            namePosition: adData.namePosition,
            descriptionPosition: adData.descriptionPosition,
            sponsorLogoPosition: adData.sponsorLogoPosition,
            linkPosition: adData.linkPosition,
          }
          setElementPositions(positions)
          setInitialElementPositions(positions)

          const isVideo =
            eventImage[0]?.type?.startsWith('video/') ||
            isVideoFile(adData.eventImage.key)
          setEventMediaType(
            isVideo ? EVENT_MEDIA_OPTIONS.VIDEO : EVENT_MEDIA_OPTIONS.IMAGE
          )
        } catch {
          showErrorToast('Failed to load ad data')
        } finally {
          setIsDataLoading(false)
        }
      }
    }

    loadData()
  }, [advertisementData])

  // Watch form values for real-time preview updates
  const watchedValues = useWatch({
    control: form.control,
    name: [
      'adName',
      'adDescription',
      'linkText',
      'redirectionLink',
      'adType',
      'adStartDate',
      'adEndDate',
      'showTitle',
      'showDescription',
    ],
  })

  const [
    adName,
    adDescription,
    linkText,
    redirectionLink,
    adType,
    adStartDate,
    adEndDate,
    showTitle,
    showDescription,
  ] = watchedValues

  useEffect(() => {
    if (adType) {
      form.resetField('adStartDate')
      form.resetField('adEndDate')
    }
  }, [adType])

  const { data: availableAdDate } = useQuery({
    queryKey: [SPONSOR_QUERY_KEYS.availableAdDates, adType, adId],
    queryFn: () =>
      getAvailableADDates({
        placement: adType,
        sponsorshipId: id!,
        ...(adId && { advertisementId: adId }),
      }),
    enabled: !!id && !!adType,
  })

  const availableADDatedata: AvailableADDatesType = availableAdDate?.data
  const heroDuration = availableADDatedata?.heroDuration

  const normalizedAvailableDates = useMemo(() => {
    return getNormalizedAdDates(availableADDatedata)
  }, [availableADDatedata])

  const isStartDateInPast = () => {
    if (!adStartDate || !adId) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const startDate = new Date(adStartDate)
    startDate.setHours(0, 0, 0, 0)
    return startDate < today
  }

  const isStartInPast = isStartDateInPast()

  const dateValidation = createAdDateValidator(
    normalizedAvailableDates,
    adStartDate,
    adEndDate,
    heroDuration,
    !!adId,
    isStartInPast
  )

  const validateFormDate = (): boolean => {
    let hasErrors = false

    form.clearErrors('adStartDate')
    form.clearErrors('adEndDate')

    if (adStartDate) {
      const startError = dateValidation.getError(adStartDate, 'start')
      if (startError) {
        form.setError('adStartDate', {
          type: 'manual',
          message: startError,
        })
        hasErrors = true
      }
    }

    if (adEndDate) {
      const endError = dateValidation.getError(adEndDate, 'end')
      if (endError) {
        form.setError('adEndDate', {
          type: 'manual',
          message: endError,
        })
        hasErrors = true
      }
    }

    return !hasErrors
  }

  useEffect(() => {
    validateFormDate()
  }, [adStartDate, adEndDate])

  const handleSponsorLogoUpload = (file: File | null) => {
    form.setValue('sponsorLogo', file, {
      shouldValidate: true,
      shouldDirty: true,
    })
    if (file) {
      const url = URL.createObjectURL(file)
      setSponsorLogoUrl(url)
    } else {
      setSponsorLogoUrl(null)
    }
  }

  const handleEventImageUpload = (file: File | null) => {
    form.setValue('eventImage', file, {
      shouldValidate: true,
      shouldDirty: true,
    })
    if (file) {
      const url = URL.createObjectURL(file)
      setEventImageUrl(url)
      const isVideo = file.type.startsWith('video/')
      setEventMediaType(
        isVideo ? EVENT_MEDIA_OPTIONS.VIDEO : EVENT_MEDIA_OPTIONS.IMAGE
      )
    } else {
      setEventImageUrl(null)
      setEventMediaType(EVENT_MEDIA_OPTIONS.IMAGE)
    }
  }

  const transformDataToPayload = async (
    data: UploadAdFormData
  ): Promise<SponsorEventAdvertisementPayload> => {
    let sponsorLogoKey = null
    if (data?.sponsorLogo) {
      const sponsorLogoResponse = await uploadSponsorLogo([data.sponsorLogo])
      sponsorLogoKey = sponsorLogoResponse?.[0]
      if (!sponsorLogoKey) {
        throw new Error('Failed to upload sponsor logo')
      }
    }

    const eventImageResponse = await uploadAdvertisementEventImage([
      data.eventImage,
    ])
    const eventImageKey = eventImageResponse?.[0]

    if (!eventImageKey) {
      throw new Error('Failed to upload event image')
    }

    let sponsorLogoType = null
    if (sponsorLogoKey) {
      sponsorLogoType = getMediaType(sponsorLogoKey)
    }
    const eventImageType = getMediaType(eventImageKey)

    // Default positions (percentage-based) for each ad type
    // These match the default positions used in the banner components
    const getDefaultPositionsForAdType = (adType: string) => {
      switch (adType) {
        case 'hero': // Home Hero Section Banner (1030x300px)
          return {
            namePosition: { x: 3.88, y: 18.67 }, // Left-aligned, top spacing
            descriptionPosition: { x: 3.88, y: 33.33 },
            linkPosition: { x: 3.88, y: 80 }, // Bottom-left
            sponsorLogoPosition: { x: 82.52, y: 80 }, // Bottom-right
          }
        case 'in_feed_bursts': // In-Feed Native Card (360x500px)
          return {
            namePosition: { x: 10, y: 24 }, // Centered with padding
            descriptionPosition: { x: 10, y: 64 },
            sponsorLogoPosition: { x: 10, y: 4 }, // Top-left
            linkPosition: { x: 10, y: 88 }, // Bottom
          }
        case 'right_rail': // Inline/Right Rail Banner (600x300px)
          return {
            namePosition: { x: 4, y: 8 }, // Top-left with padding
            descriptionPosition: { x: 4, y: 33.33 }, // Middle-left
            linkPosition: { x: 4, y: 76.67 }, // Bottom-left
            sponsorLogoPosition: { x: 80, y: 76.67 }, // Bottom-right
          }
        default: // Fallback (general percentage-based positioning)
          return {
            namePosition: { x: 5, y: 10 },
            descriptionPosition: { x: 5, y: 30 },
            linkPosition: { x: 5, y: 70 },
            sponsorLogoPosition: { x: 75, y: 70 },
          }
      }
    }

    const defaultPositions = getDefaultPositionsForAdType(data.adType)

    const payload: SponsorEventAdvertisementPayload = {
      ...(!adId && { sponsorshipId: id!, placement: data.adType }),
      name: data.adName,
      description: data.adDescription,
      startDate: new Date(data.adStartDate.setHours(0, 0, 0, 0)),
      endDate: new Date(data.adEndDate.setHours(23, 59, 59, 999)),
      link: data.redirectionLink,
      linkText: data.linkText,
      ...(sponsorLogoKey && { sponsorLogo: sponsorLogoKey }),
      eventImage: eventImageKey,
      ...(sponsorLogoType && { sponsorLogoType }),
      eventImageType,
      showTitle: data.showTitle ?? true,
      showDescription: data.showDescription ?? true,
      // Include position if explicitly set, or use default if element exists and showTitle/showDescription is true
      ...(data.showTitle &&
        data.adName && {
          namePosition:
            elementPositions.namePosition || defaultPositions.namePosition,
        }),
      ...(data.showDescription &&
        data.adDescription && {
          descriptionPosition:
            elementPositions.descriptionPosition ||
            defaultPositions.descriptionPosition,
        }),
      ...(data.linkText && {
        linkPosition:
          elementPositions.linkPosition || defaultPositions.linkPosition,
      }),
      ...(sponsorLogoKey && {
        sponsorLogoPosition:
          elementPositions.sponsorLogoPosition ||
          defaultPositions.sponsorLogoPosition,
      }),
    }

    return payload
  }

  const onSubmit = async (data: UploadAdFormData) => {
    try {
      const isValidDate = validateFormDate()
      if (!isValidDate) {
        showErrorToast(
          'Validation Error',
          'Please fix the date errors before submitting'
        )
        return
      }
      const payload = await transformDataToPayload(data)
      if (adId) {
        updateAdvertisementMutation({ id: adId, payload })
      } else {
        createSponsorEventAdvertisementMutation(payload)
      }
    } catch (error) {
      console.error('Error uploading advertisement:', error)
    }
  }

  const onError = () => {
    showErrorToast(
      'Validation Error',
      'Please fill all required fields correctly'
    )
  }

  const handleCancel = () => {
    const redirectionId = sponsorShipId || id
    navigate(
      PATHROUTES.DASHBOARD.SPONSORSHIP_DETAILS_WITH_ID(redirectionId || '')
    )
  }

  const handleAdFormatSpecs = () => {
    setIsAdFormatDialogOpen(true)
  }

  const shouldShowPreview = Boolean(
    adName?.trim() ||
      adDescription?.trim() ||
      linkText?.trim() ||
      sponsorLogoUrl ||
      eventImageUrl ||
      adType
  )

  const adPlacementTypes: AdPlacementType[] = adPlacementTypesData?.data

  // Banner component mapping
  const bannerComponents: Record<
    string,
    React.FC<{
      data: BannerData
      editable?: boolean
      onPositionChange?: (positions: {
        namePosition?: Position
        descriptionPosition?: Position
        sponsorLogoPosition?: Position
        linkPosition?: Position
      }) => void
    }>
  > = {
    'home-hero-section': HomeHeroSectionBanner,
    'home-infeed-native-card': HomeInFeedNativeCardBanner,
    'inline-banner': InlineBanner,
  }

  const getComponentKeyFromPlacementName = (
    placementName: string
  ): string | null => {
    const nameMapping: Record<string, string> = {
      hero: 'home-hero-section',
      in_feed_bursts: 'home-infeed-native-card',
      right_rail: 'inline-banner',
    }
    return nameMapping[placementName] || null
  }

  const selectedPlacement = adPlacementTypes?.find(type => type.key === adType)
  const componentKey = selectedPlacement
    ? getComponentKeyFromPlacementName(selectedPlacement.key)
    : null

  let BannerComponent: React.FC<{
    data: BannerData
    editable?: boolean
    onPositionChange?: (positions: {
      namePosition?: Position
      descriptionPosition?: Position
      sponsorLogoPosition?: Position
      linkPosition?: Position
    }) => void
  }> | null = null
  if (componentKey && bannerComponents[componentKey]) {
    BannerComponent = bannerComponents[componentKey]
  } else if (shouldShowPreview) {
    BannerComponent = HomeHeroSectionBanner
  }

  const bannerData = {
    adName,
    adDescription,
    linkText,
    redirectionLink,
    sponsorLogoUrl,
    eventImageUrl,
    eventMediaType,
    showTitle,
    showDescription,
    namePosition: elementPositions.namePosition,
    descriptionPosition: elementPositions.descriptionPosition,
    linkPosition: elementPositions.linkPosition,
    sponsorLogoPosition: elementPositions.sponsorLogoPosition,
  }

  useEffect(() => {
    const currentUrl = sponsorLogoUrl
    return () => {
      if (currentUrl && currentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentUrl)
      }
    }
  }, [sponsorLogoUrl])

  useEffect(() => {
    const currentUrl = eventImageUrl
    return () => {
      if (currentUrl && currentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentUrl)
      }
    }
  }, [eventImageUrl])

  function getUploadText() {
    if (isUploadingSponsorLogo) return 'Uploading Sponsor Image...'
    if (isUploadingEventImage) return 'Uploading Event Image...'
    if (isCreatingAdvertisement || isUpdatingAdvertisement)
      return 'Uploading Ad...'
    return adId ? 'Edit Advertisement' : 'Upload Advertisement'
  }

  const isFormDirty = form.formState.isDirty
  const isEditMode = !!adId

  const hasPositionsChanged = () => {
    if (!isEditMode) return false

    const hasChanged =
      JSON.stringify(elementPositions.namePosition) !==
        JSON.stringify(initialElementPositions.namePosition) ||
      JSON.stringify(elementPositions.descriptionPosition) !==
        JSON.stringify(initialElementPositions.descriptionPosition) ||
      JSON.stringify(elementPositions.sponsorLogoPosition) !==
        JSON.stringify(initialElementPositions.sponsorLogoPosition) ||
      JSON.stringify(elementPositions.linkPosition) !==
        JSON.stringify(initialElementPositions.linkPosition)

    return hasChanged
  }

  const hasAnyFieldFilled = () => {
    if (isEditMode) return true
    const values = form.getValues()
    return Boolean(
      values.adName?.trim() ||
        values.adDescription?.trim() ||
        values.linkText?.trim() ||
        values.redirectionLink?.trim() ||
        values.adType ||
        values.adStartDate ||
        values.adEndDate ||
        values.sponsorLogo ||
        values.eventImage
    )
  }

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (navigationSuccessRef.current) {
      return false
    }
    if (form.formState.isSubmitSuccessful) {
      return false
    }

    if (!isEditMode) {
      return false
    }
    return currentLocation.pathname !== nextLocation.pathname
  })

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowBlockDialog(true)
    }
  }, [blocker.state])

  const handleConfirmNavigation = () => {
    setShowBlockDialog(false)
    blocker.proceed?.()
  }

  const handleCancelNavigation = () => {
    setShowBlockDialog(false)
    blocker.reset?.()
  }

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      setShowBlockDialog(false)
      blocker.reset?.()
    }
  }

  if (adId && (isLoadingAdData || isDataLoading)) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-30px)]">
        <CustomLoader />
      </div>
    )
  }

  return (
    <>
      <main
        className="@container/main flex flex-1 flex-col p-6"
        id="main-content"
        tabIndex={-1}
        aria-label="Upload Advertisement Main Content"
      >
        {/* Breadcrumb Navigation */}
        <Breadcrumb className="mb-2.5">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbRouterLink
                to={PATHROUTES.DASHBOARD.SPONSORSHIP_MANAGEMENT}
                className="text-ss-neutral-600 hover:text-ss-blue-500 font-medium"
              >
                Ad Management
              </BreadcrumbRouterLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-ss-neutral-400" />
            <BreadcrumbItem>
              <BreadcrumbRouterLink
                to={PATHROUTES.DASHBOARD.SPONSORSHIP_DETAILS_WITH_ID(
                  sponsorShipId || id || ''
                )}
                className="text-ss-neutral-600 hover:text-ss-blue-500 font-medium"
              >
                Sponsorship Details
              </BreadcrumbRouterLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-ss-neutral-400" />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-ss-blue-500 font-medium">
                {adId ? 'Edit Ad' : 'Upload Ad'}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page Header */}
        <div className="flex flex-col gap-2.5 mb-4">
          <div className="flex items-start sm:items-center justify-between flex-wrap gap-4 flex-col sm:flex-row">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-ss-neutral-950 leading-8 mb-0.5">
                {adId ? 'Edit Advertisement' : 'Create New Advertisement'}
              </h1>
              <p className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                Upload images or videos with automatic format validation and
                preview
              </p>
            </div>

            <Button
              variant="ghost"
              onClick={handleAdFormatSpecs}
              className=" flex items-center gap-2 text-ss-blue-800 hover:text-ss-blue-900 hover:bg-transparent"
              aria-label="View advertisement format specifications"
            >
              <Info />
              Ad Format Specifications
            </Button>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl border border-ss-neutral-50 overflow-hidden">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, onError)}
              className="h-full"
            >
              <div className="flex gap-4 p-4 min-h-[600px] flex-col-reverse xl:flex-row">
                {/* Left Column - Form Fields */}
                <div className="flex-1 space-y-4">
                  {/* Ad Type & Placement */}
                  <FormField
                    control={form.control}
                    name="adType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel
                          variant="required"
                          className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                        >
                          Ad Type & Placement
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={!!adId}
                        >
                          <FormControl>
                            <SelectTrigger
                              size="lg"
                              className="h-11 border-ss-neutral-100 w-full"
                            >
                              <SelectValue placeholder="Select a type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {adPlacementTypes?.map(type => (
                              <SelectItem key={type.key} value={type.key}>
                                {type.placement}
                              </SelectItem>
                            ))}
                          </SelectContent>
                          {field.value === 'hero' && heroDuration && (
                            <p className="text-sm text-ss-neutral-500 mt-1">
                              (Hero Advertisement visibility duration is{' '}
                              {heroDuration}{' '}
                              {heroDuration === 1 ? 'day' : 'days'})
                            </p>
                          )}
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Advertisement Name */}
                  <FormField
                    control={form.control}
                    name="adName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel
                          variant="required"
                          className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                        >
                          Advertisement Title
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <CustomRichTextEditor
                              value={field.value}
                              onChange={value =>
                                field.onChange(value === '<p></p>' ? '' : value)
                              }
                              placeholder="e.g., Summer Wellness Retreat"
                              toolbarFeatures={['fontSize', 'textColor']}
                              height={'100px'}
                            />
                            <span className="absolute right-2 bottom-15 text-xs text-ss-neutral-400">
                              {getPlainTextLength(adName || '')}/52
                            </span>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="showTitle"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border border-ss-neutral-100 p-3">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium text-ss-neutral-950">
                            Show Title in Advertisement.
                          </FormLabel>
                          <div className="text-xs text-ss-neutral-600">
                            Toggle to display or hide the title in the
                            advertisement.
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Advertisement Description */}
                  <FormField
                    control={form.control}
                    name="adDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel
                          variant="required"
                          className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                        >
                          Advertisement Description
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <CustomRichTextEditor
                              value={field.value}
                              onChange={value =>
                                field.onChange(value === '<p></p>' ? '' : value)
                              }
                              placeholder="Add a clear and engaging description of your event."
                              toolbarFeatures={['fontSize', 'textColor']}
                              height={'200px'}
                            />
                            <span className="absolute right-2 bottom-15 text-xs text-ss-neutral-400">
                              {getPlainTextLength(adDescription || '')}/190
                            </span>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="showDescription"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border border-ss-neutral-100 p-3">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium text-ss-neutral-950">
                            Show Description in Advertisement.
                          </FormLabel>
                          <div className="text-xs text-ss-neutral-600">
                            Toggle to display or hide the description in the
                            advertisement.
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Date Fields Row */}
                  <div className="flex flex-wrap gap-4">
                    <FormField
                      control={form.control}
                      name="adStartDate"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel
                            variant="required"
                            className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                          >
                            Ad Start Date
                          </FormLabel>
                          <FormControl>
                            <DatePicker
                              disabled={!adType || isStartDateInPast()}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select start date"
                              className="h-11 border-ss-neutral-100"
                              disableDate={date =>
                                dateValidation.isDisabled(date)
                              }
                              clearable={!adId}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="adEndDate"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel
                            variant="required"
                            className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                          >
                            Ad End Date
                          </FormLabel>
                          <FormControl>
                            <DatePicker
                              disabled={!adType}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select end date"
                              className="h-11 border-ss-neutral-100"
                              disableDate={date =>
                                dateValidation.isDisabled(date, true)
                              }
                              clearable={!adId}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  {!adType && (
                    <p className="text-xs text-ss-neutral-500 mt-1">
                      Please select an ad type first
                    </p>
                  )}

                  {/* Redirection Link */}
                  <FormField
                    control={form.control}
                    name="redirectionLink"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel
                          variant="required"
                          className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                        >
                          Advertisement Redirection Link
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="url"
                            placeholder="e.g., http://www.faxquote.com"
                            className="h-11 border-ss-neutral-100 placeholder:text-ss-neutral-400"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Link Text */}
                  <FormField
                    control={form.control}
                    name="linkText"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel
                          variant="required"
                          className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                        >
                          Advertisement Link Text
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              placeholder="e.g., Get Your Festival Gear"
                              className="h-11 border-ss-neutral-100 placeholder:text-ss-neutral-400 pr-16"
                              maxLength={40}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-ss-neutral-400">
                              {linkText?.length || 0}/40
                            </span>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Sponsored by Upload */}
                  <FormField
                    control={form.control}
                    name="sponsorLogo"
                    render={() => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-ss-neutral-950 leading-4.5 ">
                          Sponsor Logo
                          <span className="text-ss-neutral-500">
                            (Logo upload is optional)
                          </span>
                        </FormLabel>
                        <span className="text-sm text-ss-neutral-500 mb-1 block">
                          Upload your brand logo. This logo appears alongside
                          your advertisement across the SlopeSync platform.
                          Accepted formats: JPEG, PNG, SVG (max 150KB).
                        </span>
                        <FormControl>
                          <FileUpload
                            onFileSelect={handleSponsorLogoUpload}
                            acceptedFileTypes={fileTypes}
                            maxFileSize={sponsorLogoSize}
                            hint="JPEG, PNG, SVG (max. 150KB)"
                            previewImage={sponsorLogoUrl || undefined}
                            previewImageHeadText="Current Sponsor Logo"
                            previewImageSubText="Existing Sponsor Logo"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Event Image Upload */}
                  <FormField
                    control={form.control}
                    name="eventImage"
                    render={() => (
                      <FormItem>
                        <FormLabel
                          variant="required"
                          className="text-sm font-semibold text-ss-neutral-950 leading-4.5 "
                        >
                          Event Image/Video
                        </FormLabel>
                        <FormControl>
                          <FileUpload
                            onFileSelect={handleEventImageUpload}
                            acceptedFileTypes={eventImageVideoTypes}
                            maxFileSize={eventImageSize}
                            hint="JPEG, PNG, GIF, MP4, MOV, WEBM, AVI (max. 5MB)"
                            previewImage={eventImageUrl || undefined}
                            previewImageHeadText="Current Event Image/Video"
                            previewImageSubText="Existing Event Image/Video"
                            isVideo={
                              eventMediaType === EVENT_MEDIA_OPTIONS.VIDEO
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Divider */}
                <div className="w-px bg-ss-neutral-50 min-h-full" />

                {/* Right Column - Preview */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-ss-neutral-950 leading-7">
                      Preview
                    </h2>
                  </div>

                  {!shouldShowPreview || !BannerComponent ? (
                    /* Default Empty State */
                    <div className="bg-ss-neutral-50 rounded-xl h-[600px] flex items-center justify-center relative">
                      <div className="flex flex-col items-center gap-2.5 text-center max-w-xs">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-9 px-2.5 rounded-full border-ss-neutral-200 bg-white"
                        >
                          <EyeOpenIcon />
                        </Button>
                        <p className="text-sm font-normal text-ss-neutral-600 leading-4.5 ">
                          {adType
                            ? 'Fill in the details to preview your selected banner type.'
                            : 'Select an ad type and fill in the details to preview how it will appear on the website.'}
                        </p>
                      </div>
                    </div>
                  ) : /* Dynamic Banner Preview */
                  componentKey !== 'home-hero-section' ? (
                    <BannerComponent
                      data={bannerData}
                      editable={true}
                      onPositionChange={positions => {
                        // Map banner component position names back to API names
                        setElementPositions({
                          namePosition: positions.namePosition,
                          descriptionPosition: positions.descriptionPosition,
                          linkPosition: positions.linkPosition,
                          sponsorLogoPosition: positions.sponsorLogoPosition,
                        })
                      }}
                    />
                  ) : (
                    <div className="flex w-full items-center justify-center py-20">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsHeroPreviewOpen(true)}
                        className="h-9 px-3 gap-2"
                      >
                        <Maximize2 className="w-4 h-4" />
                        Preview Hero Banner in Full Size
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="bg-white border-t border-ss-neutral-50 px-6 py-4 rounded-b-xl">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="h-9 px-4 border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-neutral-50 font-semibold"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      isUploadingSponsorLogo ||
                      isUploadingEventImage ||
                      isCreatingAdvertisement ||
                      isUpdatingAdvertisement ||
                      (isEditMode && !isFormDirty && !hasPositionsChanged()) ||
                      (!isEditMode && !hasAnyFieldFilled())
                    }
                  >
                    {(isUploadingSponsorLogo ||
                      isUploadingEventImage ||
                      isCreatingAdvertisement ||
                      isUpdatingAdvertisement) && (
                      <Loader className="mr-2 animate-spin" />
                    )}
                    {getUploadText()}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </main>

      {/* Ad Format Specifications Dialog */}
      <AdFormatSpecificationsDialog
        open={isAdFormatDialogOpen}
        onOpenChange={setIsAdFormatDialogOpen}
      />

      {/* Hero Banner Full Size Preview Dialog */}
      <Dialog open={isHeroPreviewOpen} onOpenChange={setIsHeroPreviewOpen}>
        <DialogContent className="min-w-[80%] max-w-[1080px] p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle>Edit Hero Banner - Full Size Preview</DialogTitle>
            <DialogDescription>
              Drag and drop the elements to position them on the banner. The
              banner is shown at full size for accurate positioning.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-6">
            {componentKey === 'home-hero-section' && BannerComponent && (
              <BannerComponent
                data={bannerData}
                editable={true}
                onPositionChange={positions => {
                  // Map banner component position names back to API names
                  setElementPositions({
                    namePosition: positions.namePosition,
                    descriptionPosition: positions.descriptionPosition,
                    linkPosition: positions.linkPosition,
                    sponsorLogoPosition: positions.sponsorLogoPosition,
                  })
                }}
              />
            )}
          </div>
          <DialogFooter className="px-6 pb-6 pt-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsHeroPreviewOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showBlockDialog}
        onOpenChange={handleDialogOpenChange}
        aria-labelledby="unsaved-changes-title"
        aria-describedby="unsaved-changes-desc"
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle id="unsaved-changes-title">
              Unsaved Changes
            </DialogTitle>
            <DialogDescription id="unsaved-changes-desc">
              You have unsaved changes that will be lost if you leave this page.
              Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelNavigation}
              aria-label="Stay on current page"
            >
              Stay Here
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmNavigation}
              aria-label="Discard changes and leave"
            >
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default UploadAdData
